import OpenFolder from 'components/Icons/OpenFolder';
import Studio from 'components/Icons/Studio';
import { getAccountShortName } from 'constants/integrations';
import { List } from 'immutable';
import React, { ReactElement } from 'react';
import { useTranslator } from '@bluebeam/react-i18n';
import BreadcrumbsDropdown from './BreadcrumbsDropdown';

type BreadcrumbFolder = {
  label: string;
  id: string;
};

export type Breadcrumb = {
  key: string;
  label: string;
  onClick: () => void;
};
interface Props {
  provider: {
    type: string;
    onClick: () => void;
  };
  site?: {
    id: string;
    label: string;
    onClick: () => void;
  };
  drive?: {
    id: string;
    label: string;
    onClick: () => void;
  };
  folders?: {
    value: BreadcrumbFolder[];
    onClick: (breadcrumb: BreadcrumbFolder) => void;
  };
}

const Breadcrumbs: React.FC<Props> = (props: Props) => {
  const { drive, folders, provider, site } = props;

  const breadcrumbList = List<Breadcrumb>(
    (site
      ? [
          {
            key: site.id,
            label: site.label,
            onClick: site.onClick,
          },
        ]
      : []
    )
      .concat(
        drive
          ? [
              {
                key: drive.id,
                label: drive.label,
                onClick: drive.onClick,
              },
            ]
          : []
      )
      .concat(
        folders
          ? folders.value.map((bc) => ({
              key: bc.id,
              label: bc.label,
              onClick: () => {
                folders.onClick(bc);
              },
            }))
          : []
      )
  );

  const lastBreadcrumb = breadcrumbList.get(-1);
  const nextToLastBreadcrumb = breadcrumbList.get(-2);
  const remainingBreadcrumbs = breadcrumbList.slice(0, -2);
  const { translate } = useTranslator(['integrationsPage']);

  const providerName =
    getAccountShortName(provider.type) === 'Account'
      ? translate('integrationsPage:accountDefaultShortName')
      : getAccountShortName(provider.type);

  const Caret = () => <div className="mr2 font-size-4">{'>'}</div>;

  const Icon = (): ReactElement => {
    switch (provider.type) {
      case 'studio':
        return <Studio width="18px" height="18px" />;
      default:
        return <OpenFolder />;
    }
  };

  return (
    <div className="flex row hrem-2 items-center">
      <div className="color-primary">
        <Icon />
      </div>

      <div
        className={`${
          folders && folders.value.length > 0 ? 'underline' : ''
        } color-primary cursor-pointer font-size-4 mx2`}
        onClick={provider.onClick}
      >
        {providerName}
      </div>
      {remainingBreadcrumbs.size > 0 && <Caret />}
      {remainingBreadcrumbs.size > 0 && (
        <div className="mr2">
          <BreadcrumbsDropdown breadcrumbs={remainingBreadcrumbs} />
        </div>
      )}
      {nextToLastBreadcrumb && <Caret />}
      {nextToLastBreadcrumb && (
        <div
          className="color-primary underline font-size-4 cursor-pointer mr2"
          onClick={nextToLastBreadcrumb.onClick}
        >
          {nextToLastBreadcrumb.label}
        </div>
      )}
      {lastBreadcrumb && <Caret />}
      {lastBreadcrumb && (
        <div className="font-size-4 mr2">{lastBreadcrumb.label}</div>
      )}
    </div>
  );
};

export default Breadcrumbs;
